"use client";

import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
export default function TheVanguardStandard() {
  const {
    ref: headingRef,
    isVisible: headingVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.2
  });
  const {
    ref: imageRef,
    isVisible: imageVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.2
  });
  return <section data-section-id="1767" id="the-vanguard-standard" className="relative bg-background text-foreground py-24 md:py-44 overflow-hidden">
      {}
      <div aria-hidden="true" className="absolute top-0 left-0 right-0 h-6 bg-primary" style={{
      backgroundImage: "repeating-linear-gradient(45deg, hsl(215 45% 7%) 0 6px, hsl(215 45% 7%) 6px 12px), repeating-linear-gradient(-45deg, transparent 0 6px, hsl(155 85% 35% / 0.06) 6px 12px)"
    }} />

      <div className="mx-auto max-w-[1360px] px-6 md:px-8 lg:px-16">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16">
          {}
          <div ref={imageRef} className="lg:col-span-5 relative">
            {}
            <div aria-hidden="true" className="absolute -left-4 -top-4 -right-4 -bottom-4 opacity-[0.04] pointer-events-none" style={{
            backgroundImage: "repeating-linear-gradient(45deg, hsl(215 45% 7%) 0 8px, transparent 8px 16px), repeating-linear-gradient(-45deg, hsl(215 45% 7%) 0 8px, transparent 8px 16px)"
          }} />

            <div className="relative w-full h-[70vh] lg:h-[720px] overflow-hidden">
              {}
              <div aria-hidden="true" className="absolute inset-y-0 left-0 w-1 z-20 transition-transform duration-700 ease-out" style={{
              backgroundColor: "hsl(155 85% 35%)",
              transform: imageVisible ? "translateX(calc(100vw))" : "translateX(0)"
            }} />
              <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1701321/img/vanguard_founder_portrait.webp" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1701321/img/vanguard_founder_portrait.webp-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1701321/img/vanguard_founder_portrait.webp-wvc-sizes" alt="Founder of Vanguard Shield Insurance in a professional setting" loading="lazy" decoding="async" className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-700 ${imageVisible ? "opacity-100" : "opacity-0"}`} style={{
              filter: "saturate(0.88)"
            }} />

              {}
              <span aria-hidden="true" className="absolute top-3 left-3 w-1.5 h-1.5 rounded-lg bg-foreground z-10" />
              <span aria-hidden="true" className="absolute top-3 right-3 w-1.5 h-1.5 rounded-lg bg-foreground z-10" />
              <span aria-hidden="true" className="absolute bottom-3 left-3 w-1.5 h-1.5 rounded-lg bg-foreground z-10" />
              <span aria-hidden="true" className="absolute bottom-3 right-3 w-1.5 h-1.5 rounded-lg bg-foreground z-10" />

              {}
              <div className="absolute top-4 right-6 z-10">
                <span className="font-mono text-[0.7rem] font-semibold uppercase px-2 py-1 bg-primary text-primary-foreground" style={{
                letterSpacing: "0.12em"
              }}>
                  [J. STERLING / FOUNDER]
                </span>
              </div>

              {}
              <div className="absolute bottom-4 left-6 z-10">
                <span className="font-mono text-[0.7rem] font-semibold uppercase px-2 py-1 text-primary-foreground" style={{
                backgroundColor: "hsl(155 85% 35%)",
                color: "hsl(215 45% 7%)",
                letterSpacing: "0.12em"
              }}>
                  [EST. 1998]
                </span>
              </div>
            </div>
          </div>

          {}
          <div ref={headingRef} className={`lg:col-span-7 lg:pl-12 xl:pl-20 transition-all duration-500 ease-out ${headingVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`}>
            {}
            <div className="flex items-center gap-3 mb-6">
              <span aria-hidden="true" className="inline-block w-2.5 h-2.5" style={{
              backgroundColor: "hsl(155 85% 35%)"
            }} />
              <span className="font-mono text-xs md:text-sm font-semibold uppercase text-foreground" style={{
              letterSpacing: "0.18em"
            }}>
                § 04 — THE VANGUARD STANDARD
              </span>
            </div>

            {}
            <h2 className="font-default font-black uppercase text-foreground mb-10" style={{
            fontSize: "clamp(2.5rem, 5.4vw, 5rem)",
            letterSpacing: "-0.015em",
            lineHeight: "0.92"
          }}>
              I BUILT THIS FIRM ON{" "}
              <em className="not-italic" style={{
              fontStyle: "italic",
              color: "hsl(155 85% 35%)"
            }}>
                CERTAINTY.
              </em>{" "}
              WE PROTECT YOUR FUTURE THE SAME WAY.
            </h2>

            {}
            <p className="text-base md:text-lg leading-relaxed text-foreground/85 mb-8">
              My father spent his career in risk management, teaching me that insurance isn't just a policy—it's a promise. He kept his records meticulous, his advice transparent, and his commitment to clients absolute. When I founded Vanguard Shield in 1998, I brought that same discipline to the industry: clear coverage, honest communication, and a firm that stands as a fortress for your family's assets.
            </p>

            {}
            <div className="flex items-center mb-8">
              <span className="flex-1 h-px bg-muted-foreground/60" />
              <span aria-hidden="true" className="w-1.5 h-1.5 ml-2" style={{
              backgroundColor: "hsl(155 85% 35%)"
            }} />
            </div>

            {}
            <p className="text-base md:text-lg leading-relaxed text-foreground/85 mb-10">
              The insurance landscape is often cluttered with fine print and confusing jargon that hides the only truth that matters: are you truly protected? We don't do clutter. We provide streamlined, high-security coverage plans and a dedicated team that knows your name, not just your policy number. We are here to ensure that when the unexpected happens, you have a shield that holds.
            </p>

            {}
            <div className="relative pl-8 mb-10 ml-0 md:ml-12" style={{
            borderLeft: "4px solid hsl(155 85% 35%)"
          }}>
              <p className="font-default font-bold italic text-foreground uppercase" style={{
              fontSize: "clamp(1.375rem, 2.1vw, 2rem)",
              lineHeight: "1.15",
              letterSpacing: "-0.005em"
            }}>
                &ldquo;If I wouldn't trust a policy to protect my own family's legacy, it doesn't leave our office. That is the Vanguard standard.&rdquo;
              </p>
            </div>

            {}
            <div className="flex items-center mb-8">
              <span className="flex-1 h-px bg-muted-foreground/60" />
              <span aria-hidden="true" className="w-1.5 h-1.5 ml-2" style={{
              backgroundColor: "hsl(155 85% 35%)"
            }} />
            </div>

            {}
            <p className="text-base md:text-lg leading-relaxed text-foreground/85 mb-12">
              Twenty-five years later, we have secured thousands of homes, businesses, and futures. Our clients stay with us because we don't just sell insurance; we provide peace of mind through rigorous, proactive protection. That is our commitment. There isn't a more important version of our story.
            </p>

            {}
            <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-6 mb-10">
              <div>
                <span className="font-mono text-xs font-semibold uppercase text-foreground block mb-3" style={{
                letterSpacing: "0.18em"
              }}>
                  — JONATHAN STERLING, CEO / FOUNDER
                </span>
                {}
                <svg width="180" height="56" viewBox="0 0 180 56" fill="none" xmlns="http://www.w3.org/2000/svg" aria-label="Signature of Jonathan Sterling" className="text-foreground">
                  <path d="M4 38 C 12 18, 22 14, 28 30 C 32 42, 36 22, 44 28 C 50 32, 48 14, 58 22 C 66 30, 60 42, 72 36 C 84 30, 80 14, 92 20 C 104 26, 100 42, 112 38 C 122 34, 118 18, 130 24 C 142 30, 138 42, 152 36 C 162 32, 168 22, 176 26" stroke="currentColor" strokeWidth="2" strokeLinecap="round" fill="none" />
                  <path d="M16 46 L 168 46" stroke="currentColor" strokeWidth="1" strokeLinecap="round" opacity="0.4" />
                </svg>
              </div>
            </div>

            {}
            <Link to="/ClaimsCenterPage" className="group inline-flex items-center gap-2 font-mono text-sm font-semibold uppercase text-foreground hover:text-foreground transition-colors" style={{
            letterSpacing: "0.12em"
          }}>
              <span className="border-b border-foreground/40 group-hover:border-foreground pb-0.5">
                View our claims support process
              </span>
              <span aria-hidden="true" className="transition-transform duration-200 ease-out group-hover:translate-x-1 inline-block" style={{
              color: "hsl(155 85% 35%)"
            }}>
                →
              </span>
            </Link>
          </div>
        </div>
      </div>
    </section>;
}